import React, { useState, useEffect, useContext, useRef } from "react";
import ErrorComponent from "./Error";

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import ApplicationError from '../ApplicationError';
import ErrorUtils from '../utils/ErrorUtils';

import Dialog from "./Dialog";

import LanguageContext from "../contexts/Language";
import TitleContext from "../contexts/Title";
import UtilContext from "../contexts/Util";
import UserDataContext from "../contexts/UserData";
import StatusContext from "../contexts/Status";

import AppConfig from "../AppConfig";

export default function PasswordResetRouter({ disableAcceptBtn, config, checkRecaptcha, passwordResetEmail, passwordResetPhone, passwordResetCode, passwordResetPhoneCode, passwordReset, passwordPhoneReset, setGlobalError, checkPasswordPolicy, password_valid, setPasswordValid, default_phone_region, view_disabled }) {
  const { page_status } = useContext(StatusContext);
  const { getTag, lang } = useContext(LanguageContext);
  const { user_data } = useContext(UserDataContext);

  // state
  const [occuredError, setOccuredError] = useState(false);

  // ref
  const errorData = useRef(null);

  // ダイアログを閉じる処理
  // エラー状態をリセットし、ダイアログを閉じる
  const closeDialog = () => {
    setOccuredError(false);
    errorData.current = null;
    Dialog(false);
  }

  /**
   * useEffect
   */
  useEffect(() => {
    // すでにReCAPTCHA用の要素がルートと同列に存在している場合削除する
    var targets = document.querySelectorAll(".g-recaptcha-bubble-arrow");
    for (var i = 0; i < targets.length; i++) {
      targets[i].parentNode.remove();
    }
  }, []);

  /**
   * useEffect
   */
  useEffect(() => {
    // 画面遷移時の処理
    // エラー状態をリセットし、ダイアログが開いている場合はそれを閉じる
    closeDialog();
  }, [page_status]);

  /**
   * useEffect
   */
  useEffect(() => {
    // エラー発生判定
    if (occuredError && errorData.current) {
      // コード・画面期限切れエラーの場合、エラー画面へ遷移するため、ダイアログ表示しない
      if (errorData.current.title !== 'error.expired_code_title'
        && errorData.current.title !== 'error.passwordreset_timeout_title'
        && errorData.current.title !== "error.max_check_attempts_reached_title"
        && errorData.current.title !== 'error.invalid_operation_state_title') {
        // エラーダイアログ表示
        Dialog({
          title: getTag(errorData.current.title),
          message: getTag(errorData.current.description),
          buttonLabel: getTag('passwordreset.close'),
          btn_action: closeDialog,
          btn_cancel: closeDialog
        });
      }
    }
  }, [occuredError, getTag]);

  if (!user_data.tenant) {
    setGlobalError("page_not_found");
  }

  return (
    <div id="bc_content_layer">
      {page_status.match(/^\/password\/recover/) && <RecoverForm disableAcceptBtn={disableAcceptBtn} checkRecaptcha={checkRecaptcha} passwordResetEmail={passwordResetEmail} passwordResetPhone={passwordResetPhone} setOccuredError={setOccuredError} errorData={errorData} default_phone_region={default_phone_region} view_disabled={view_disabled} config={config} />}
      {page_status.match(/^\/password\/code/) && <CodeForm disableAcceptBtn={disableAcceptBtn} passwordResetCode={passwordResetCode} passwordResetPhoneCode={passwordResetPhoneCode} setOccuredError={setOccuredError} errorData={errorData} />}
      {page_status.match(/^\/password\/reset/) && <ResetForm disableAcceptBtn={disableAcceptBtn} passwordReset={passwordReset} passwordPhoneReset={passwordPhoneReset} checkPasswordPolicy={checkPasswordPolicy} password_valid={password_valid} setPasswordValid={setPasswordValid} setOccuredError={setOccuredError} errorData={errorData} />}
      {page_status.match(/^\/password\/finish/) && <ResetFinish setOccuredError={setOccuredError} errorData={errorData} />}
    </div>
  );
}

function RecoverForm({ disableAcceptBtn, checkRecaptcha, passwordResetEmail, passwordResetPhone, setOccuredError, errorData, default_phone_region, view_disabled, config }) {
  // state
  const [inputError, setInputError] = useState("");
  const [loginIdType, setloginIdType] = useState("");
  const [email, setEmail] = useState("");
  const [redirect, setRedirect] = useState("");
  const [processing, setProcessing] = useState(false);
  const [phone_cd, setPhoneCd] = useState("");
  const [phone, setPhone] = useState("");
  const [inactiveEmail, setInactiveEmail] = useState("inactive");
  const [inactivePhone, setInactivePhone] = useState("inactive");
  const [description, setDescription] = useState("");

  const { setTitle } = useContext(TitleContext);
  const { getTag, lang, tags } = useContext(LanguageContext);
  const { user_data } = useContext(UserDataContext);
  const { email_is_valid } = useContext(UtilContext);
  const { setPageStatus } = useContext(StatusContext);

  const regionRef = useRef("");

  // Google reCAPTCHA
  const { executeRecaptcha } = useGoogleReCaptcha();

  /***************************
   * FAQからの復帰先URL生成
   ***************************/
  const getReturnToUrl = () => {
    const query = new URLSearchParams(window.location.search);
    query.set("first", "passwordreset");
    const queryString = query.toString();
    const returnToUrl = `${window.location.protocol}//${window.location.host}/login?${queryString}`;
    return returnToUrl;
  };

  useEffect(() => {
    (async () => {
      if (user_data.email) {
        setEmail(user_data.email);
      }
      if (user_data.phone_cd) {
        setPhoneCd(user_data.phone_cd);
      }
      if (user_data.phone_region) {
        regionRef.current = user_data.phone_region;
      }
      /*
      if (user_data.phone_org) {
        setPhone(user_data.phone_org);
      }*/
      if (user_data.login_id_type) {
        setInactiveEmail(user_data.login_id_type === "email" ? "" : "inactive");
        setInactivePhone(user_data.login_id_type === "phone" ? "" : "inactive");
        setloginIdType(user_data.login_id_type);
      }
      if (user_data.login_id_type === "phone") {
        setDescription(getTag("passwordreset.phone_text"));
      }
      else {
        setDescription(getTag("passwordreset.text"));
      }

      setTitle(getTag("passwordreset.header_title"));
    })();
  }, [lang, getTag, setTitle, user_data]);

  useEffect(() => {
    if (redirect) {
      setPageStatus(redirect);
    }
  }, [redirect, setPageStatus]);

  const onChangeLoginIDType = (ev) => {
    let elm = ev.currentTarget;
    let target = elm.value;
    let isEmail = "inactive";
    let isPhone = "";
    if (target === "Email") {
      isEmail = "";
      isPhone = "inactive";
    }
    setInputError("");
    setInactiveEmail(isEmail);
    setInactivePhone(isPhone);
    setloginIdType(target.toLowerCase());
    user_data["login_id_type"] = target.toLowerCase();
    if (user_data.login_id_type === "phone") {
      setDescription(getTag("passwordreset.phone_text"));
    }
    else {
      setDescription(getTag("passwordreset.text"));
    }
  }
  const onChangeEmail = (ev) => {
    let em = ev.target.value;
    setEmail(em);
    setInputError("");
  };
  const onChangeRegion = (ev) => {
    const target = ev.target.options[ev.target.selectedIndex];
    let region = target.dataset.phoneno;
    regionRef.current = region;
    setPhoneCd(target.value);
  };
  const onChangePhone = (ev) => {
    let val = ev.target.value.replace(/[^0-9]/g, "");
    ev.target.value = val;
    setPhone(val);
  };
  const onBlurEmail = (ev) => {
    let em = ev.target.value;
    setEmail(em);
  };
  const onBlurPhone = (ev) => {
    setPhone(ev.target.value);
  };


  /**
   * メールアドレス入力検証
   */
  const validEmail = () => {
    // 入力チェック
    if (!email) {
      setInputError(getTag("error.empty_email"));
      return false;
    }

    // フォーマット検証
    if (!email_is_valid(email)) {
      setInputError(getTag("error.invalid_email"));
      return false;
    }
    return true;
  }
  /**
   * 電話番号入力検証
   */
  const validPhone = () => {
    // 入力チェック
    let errmsg = [];
    if (!regionRef.current) {
      errmsg.push(<span key="phone_empty_region">{getTag("error.empty_region")}</span>);
    }
    if (!phone) {
      if (!regionRef.current) {
        errmsg.push(<br key="phone_error_br" />);
      }
      errmsg.push(<span key="phone_empty_phone">{getTag("error.empty_phone")}</span>);
    }
    if (errmsg.length > 0) {
      setInputError(errmsg);
      return;
    }

    // フォーマット検証
    let phoneno = regionRef.current.replace("+", "") + phone;
    if (!(/^[0-9]+$/.test(phoneno))) {
      setInputError(getTag("error.invalid_phone"));
      return false;
    }

    if (phoneno.length < 8 || 15 < phoneno.length) {
      setInputError(getTag("error.invalid_phone"));
      return false;
    }
    return true;
  }

  const resetLoginId = async () => {
    if (loginIdType === "phone") {
      // 電話番号対応
      if (!validPhone()) {
        return false;
      }
    }
    else {
      //　メールアドレス対応
      if (!validEmail()) {
        return false;
      }
    }

    setInputError("");
    setProcessing(true);
    let user_data = {
      gtoken: null
    };
    try {
      // Captcha検証
      const token = await executeRecaptcha('yourAction');
      user_data.gtoken = token;
    }
    catch (err) {
      console.log(err.message);
      errorData.current = ErrorUtils.getErrorInfo(err);
      setOccuredError(true);
      setProcessing(false);
      return;
    }

    try {
      // 確認処理
      if (loginIdType === "phone") {
        user_data["phone"] = regionRef.current + phone;
        await passwordResetPhone(user_data);
      }
      else {
        user_data["email"] = email;
        await passwordResetEmail(user_data);
      }
      user_data["login_id_type"] = loginIdType;
      setRedirect("/password/code/");
    }
    catch (err) {
      console.log(err.message);
      let errdata = ErrorUtils.getErrorInfo(err);
      if (errdata.code === "invalid_phone") {
        setInputError(getTag("error." + errdata.code));
      }
      else {
        errorData.current = errdata;
        setOccuredError(true);
      }

      setProcessing(false);
      return;
    }
    setProcessing(false);
  }

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    disableAcceptBtn("PasswordReset");
    await resetLoginId();
    disableAcceptBtn("PasswordReset", false);
  };

  /***************************
   * 他画面遷移処理
   ***************************/
  const transLink = (in_target) => {
    user_data["phone_cd"] = phone_cd;
    setRedirect(in_target);
  };

  // -------------------------------
  // ログインID種別生成
  // -------------------------------
  let addLoginIDType = null;
  if (!view_disabled.disabledLoginIDType) {
    let email = getTag("passwordreset.type_email") ?? "メールアドレス（仮）";
    let phone = getTag("passwordreset.type_phone") ?? "電話番号（仮）";
    let checkedEmail = user_data.login_id_type === "email" ? "checked" : "";
    let checkedPhone = user_data.login_id_type === "phone" ? "checked" : "";
    addLoginIDType = [];
    addLoginIDType.push(
      <div id="LoginIDType" key="LoginIDType">
        <div>
          <input id="LoginIDTypeEmail" name="LoginIDTypeSelector" className="radio" type="radio" value="Email" onChange={onChangeLoginIDType} defaultChecked={checkedEmail} />
          <label className="radio_label" htmlFor="LoginIDTypeEmail">{email}</label>
        </div>
        <div>
          <input id="LoginIDTypePhone" name="LoginIDTypeSelector" className="radio" type="radio" value="Phone" onChange={onChangeLoginIDType} defaultChecked={checkedPhone} />
          <label className="radio_label" htmlFor="LoginIDTypePhone">{phone}</label>
        </div>
      </div>
    );
  }

  // -------------------------------
  // デフォルト国番号の判定と生成
  // 1. 国番号と国コードが設定されている場合、マスタに直接存在確認をする
  // 2. 国番号しかない場合、番号のみで存在確認をする
  // -------------------------------  
  const tmpRegionsNo = tags[lang].common.regions_no;
  const tmpRegionsLabel = tags[lang].common.regions_label;
  //console.dir(tmpRegionsNo);
  //console.dir(tmpRegionsLabel);
  //console.dir(default_phone_region);
  let defRegKey = phone_cd ?? null;
  let defRegPre = "phone_no_";
  let defRegNo = null;
  if (default_phone_region && default_phone_region.no) {
    defRegNo = default_phone_region.no
  }
  let defRegCountry = null;
  if (default_phone_region && default_phone_region.country) {
    defRegCountry = default_phone_region.country.toUpperCase();
  }
  //console.log(defRegNo);
  //console.log(defRegCountry);
  if (defRegNo && defRegCountry) {
    defRegKey = defRegPre + defRegCountry + "_" + defRegNo;
    if (!tmpRegionsNo[defRegKey]) {
      defRegKey = "";
    }
  }
  else if (defRegNo && !defRegKey) {
    let res = Object.entries(tmpRegionsNo).find(([key, value]) => key.endsWith("_" + defRegNo));
    //console.log(res);
    if (res) {
      defRegKey = res[0];
    }
  }
  //console.log(defRegKey);
  if (defRegKey && !phone_cd) {
    //console.log(tmpRegionsNo[defRegKey]);
    regionRef.current = tmpRegionsNo[defRegKey];
    setPhoneCd(defRegKey);
  }

  // -------------------------------
  // 国地域選択ボックスの生成
  // -------------------------------  
  let addRegionItems = [];
  let resId = "register.region_placeholder";
  let text = getTag(resId);
  //console.log(text);
  addRegionItems.push(<option key={resId} className="inactive">{text}</option>);
  if (tmpRegionsNo) {
    for (const [key, value] of Object.entries(tmpRegionsNo)) {
      let label = value;
      if (tmpRegionsLabel && tmpRegionsLabel[key]) {
        label += " " + tmpRegionsLabel[key];
        addRegionItems.push(<option key={key} value={key} data-phoneno={value}>{label}</option>);
      }
    }
  }
  //console.dir(addRegionItems);

  return (
    <div id="PasswordReset" className="form">
      <div className="input_area">
        <h1 className="title">{getTag("passwordreset.title")}</h1>
        <p className="description">{description}</p>
        <div className="input_area">
          {addLoginIDType}
          <p id="LoginIDTypeInputEmail" className={inactiveEmail}>
            <label htmlFor="email">{getTag("passwordreset.email")}</label>
            <input type="email" id="email" name="email" placeholder={getTag("passwordreset.email_placeholder")} defaultValue={email} onChange={onChangeEmail} onBlur={onBlurEmail} disabled={processing} />
          </p>
          <p id="LoginIDTypeInputPhone" className={inactivePhone}>
            <label htmlFor="regions">{getTag("passwordreset.region")}</label>
            <select name="regions" value={defRegKey} onChange={(ev) => onChangeRegion(ev)}>
              {addRegionItems}
            </select>
            <label htmlFor="phone">{getTag("passwordreset.phone")}</label>
            <input type="tel" id="phone" name="phone" placeholder={getTag("register.phone.placeholder")} defaultValue={phone} onChange={onChangePhone} onBlur={onBlurPhone} disabled={processing} maxLength={15} />
          </p>
        </div>
      </div>
      {inputError && <div id="Notification" className="error">{inputError}</div>}
      <div className="action_area">
        <p className="action_btn"><button className="btn_l acceptbtn" onClick={handleSubmit}>{getTag("passwordreset.continue")}</button></p>
      </div>
      <div className="linklist">
        <p><button className="link agreement" onClick={() => window.location.href = `${AppConfig.NEWS_URL}/faq?client_id=${config.client_id}&return_url=${encodeURIComponent(getReturnToUrl())}`}><span>{"FAQ"}</span></button></p>
        <p><button className="link agreement" onClick={() => transLink("/login")}><span>{getTag("common.return")}</span></button></p>
        {getTag("passwordreset.additional_anchor1_url") && <p><button className="link" onClick={() => window.location = getTag("passwordreset.additional_anchor1_url")}><span>{getTag("passwordreset.additionl_anchor1")}</span></button></p>}
        {getTag("passwordreset.additional_anchor2_url") && <p><button className="link" onClick={() => window.location = getTag("passwordreset.additional_anchor2_url")}><span>{getTag("passwordreset.additionl_anchor2")}</span></button></p>}
        {getTag("passwordreset.additional_anchor3_url") && <p><button className="link" onClick={() => window.location = getTag("passwordreset.additional_anchor3_url")}><span>{getTag("passwordreset.additionl_anchor3")}</span></button></p>}
      </div>
    </div>
  );
}

function CodeForm({ disableAcceptBtn, passwordResetCode, passwordResetPhoneCode, setOccuredError, errorData }) {
  // state
  const [inputError, setInputError] = useState("");
  const [code, setCode] = useState("");
  const [redirect, setRedirect] = useState("");
  const [processing, setProcessing] = useState(false);

  const { getTag, lang } = useContext(LanguageContext);
  const { setTitle } = useContext(TitleContext);
  const { user_data } = useContext(UserDataContext);
  const { setPageStatus } = useContext(StatusContext);

  useEffect(() => {
    setTitle(getTag("passwordreset.header_title"));

    let loginIdType = user_data.login_id_type;
    if (loginIdType === "phone") {
      if (!user_data.phone || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
    else {
      if (!user_data.email || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
  }, [user_data, setTitle, getTag, lang]);

  useEffect(() => {
    if (redirect) {
      setPageStatus(redirect);
    }
  }, [redirect, setPageStatus]);


  const canSubmit = (ev) => {
    let res = (!processing) && code.match(/^\d{6}$/);
    disableAcceptBtn("PasswordReset", !res);
    return res;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setProcessing(true);

    try {
      let resState = null;
      if (user_data["login_id_type"] === "phone") {
        resState = await passwordResetPhoneCode(code);
      }
      else {
        resState = await passwordResetCode(code);
      }
      if (resState !== null) {
        setRedirect("/password/reset/");
      } else {
        setInputError('invalid_code');
        // コントロール活性
        setProcessing(false);
      }
    }
    catch (err) {
      console.log(err.message);

      errorData.current = ErrorUtils.getErrorInfo(err);
      setOccuredError(true);

      // コントロール活性
      setProcessing(false);
    }
  };

  if (errorData.current) {
    if (errorData.current.title === "error.expired_code_title") {
      return <ErrorComponent title={errorData.current.title} error={errorData.current.description} redirect_to={"/password/recover"} />;
    }
    else if (errorData.current.title === "error.max_check_attempts_reached_title") {
      return <ErrorComponent title={errorData.current.title} error={errorData.current.description} redirect_to={"/password/recover"} />;
    }
  }

  return (
    <div id="PasswordReset" className="form">
      <h1 className="title">{getTag("passwordreset.title")}</h1>
      <div className="input_area">
        <p>
          <label htmlFor="code">{getTag("passwordreset.confirm_code_text")}</label>
          <input type="text" id="code" name="code" placeholder={getTag("passwordreset.confirm_code_placeholder")} defaultValue={code} onChange={(ev) => setCode(ev.target.value)} disabled={processing} maxLength={6} />
        </p>
      </div>
      {inputError && <div id="Notification" className="error">{getTag("error." + inputError)}</div>}
      <div className="action_area">
        <p className="action_btn"><button className="btn_l acceptbtn disabledbtn" disabled={!canSubmit()} onClick={handleSubmit}>{getTag("passwordreset.confirm_code_button")}</button></p>
      </div>
      <div className="linklist">
        <button id="AdditionalAnchor1" className="link" onClick={() => setRedirect("/password/recover")}><span>{getTag("common.return")}</span></button>
      </div>
    </div>
  );
}

function ResetForm({ disableAcceptBtn, passwordReset, passwordPhoneReset, checkPasswordPolicy, password_valid, setPasswordValid, setOccuredError, errorData }) {
  // state
  const [inputError, setInputError] = useState("");
  const [password, setPassword] = useState("");
  const [password0, setPassword0] = useState("");
  const [redirect, setRedirect] = useState("");
  const [processing, setProcessing] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);

  const { setTitle } = useContext(TitleContext);
  const { getTag, lang } = useContext(LanguageContext);
  const { user_data } = useContext(UserDataContext);
  const { setPageStatus } = useContext(StatusContext);
  const [timeover, setTimeover] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      console.log("Password reset is time over");
      setTimeover(true);
    }, (10 * 60 * 1000));
  }, [])

  useEffect(() => {
    setTitle(getTag("passwordreset.header_title"));

    let loginIdType = user_data.login_id_type;
    if (loginIdType === "phone") {
      if (!user_data.phone || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
    else {
      if (!user_data.email || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
  }, [user_data, setTitle, getTag, lang]);

  useEffect(() => {
    if (redirect) {
      setPageStatus(redirect);
    }
  }, [redirect, setPageStatus]);

  /*
  useEffect( () => {
    let res = password && password_valid;
  }, [password, password_valid, disableAcceptBtn]);
  */

  const onChangePassword = (ev) => {
    let pw = ev.target.value;
    setPassword(pw);
  };

  const onChangePasswordShown = (ev) => {
    const passwordShown = ev.target.checked;
    setPasswordShown(passwordShown);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setProcessing(true);
    disableAcceptBtn("PasswordReset");

    if (password) {
      setInputError("");
      if (password !== password0) {
        if (password.length > 7 && password.length < 65) {
          setPasswordValid(false);
          setPassword0(password);
          try {
            setProcessing(true);
            const check_password_result = await checkPasswordPolicy(password, user_data.email, user_data.phone);
            setProcessing(false);

            if (!check_password_result) {
              setInputError("invalid_pass_2");
            } else {
              pwReset();
            }
          }
          catch (err) {
            console.log(err.message);
            setInputError(err.message);
            setProcessing(false);
          }
        }
        else {
          setInputError("invalid_pass_2");
        }
      } else {
        if (!password_valid) {
          setInputError("invalid_pass_2");
        }
      }
    } else {
      setPassword0(password);
      setInputError("empty_password");
    }
    disableAcceptBtn("PasswordReset", false);
    setProcessing(false);
  };

  const pwReset = async () => {
    try {
      if (timeover) {
        throw new ApplicationError({ code: 'passwordreset_timeout' }, "Password reset expired.");
      }
      if (user_data.login_id_type === "phone") {
        await passwordPhoneReset(password);
      }
      else {
        await passwordReset(password);
      }
      setRedirect("/password/finish/");
    }
    catch (err) {
      console.log(err.message);
      setProcessing(false);
      errorData.current = ErrorUtils.getErrorInfo(err);
      setOccuredError(true);
    }
  }

  const passwordInputType = () => {
    if (passwordShown) {
      return 'text';
    } else {
      return 'password';
    }
  };

  if (errorData.current) {
    if (errorData.current.title === "error.passwordreset_timeout_title"
      || errorData.current.title === "error.invalid_operation_state_title") {
      return <ErrorComponent title={errorData.current.title} error={errorData.current.description} redirect_to={"/password/recover"} />;
    }
  }

  return (
    <div id="PasswordReset" className="form">
      <h1 className="title">{getTag("passwordreset.title")}</h1>
      <div className="input_area">
        <p>
          <label htmlFor="password">{getTag("passwordreset.password")}</label>
          <input type={passwordInputType()} id="password" name="password" placeholder={getTag("passwordreset.password_placeholder")} defaultValue={password} onChange={onChangePassword} disabled={processing} />
          <input type="checkbox" id="showPassword" onChange={onChangePasswordShown} /><label htmlFor="showPassword" id="showPasswordLabel">{getTag("passwordreset.show_password")}</label>
        </p>
      </div>
      {inputError && <div id="Notification" className="error">{getTag("error." + inputError)}</div>}
      <div className="action_area">
        <p className="action_btn"><button className="btn_l acceptbtn" onClick={handleSubmit}>{getTag("passwordreset.password_reset")}</button></p>
      </div>
      <div className="linklist">
        <button id="AdditionalAnchor1" className="link" onClick={() => setRedirect("/password/recover")}><span>{getTag("common.return")}</span></button>
      </div>
    </div>
  );
}

function ResetFinish() {
  const [redirect, setRedirect] = useState("");

  const { getTag, lang } = useContext(LanguageContext);
  const { setTitle } = useContext(TitleContext);
  const { user_data } = useContext(UserDataContext);
  const { setPageStatus } = useContext(StatusContext);

  useEffect(() => {
    setTitle(getTag("passwordreset.header_title"));

    let loginIdType = user_data.login_id_type;
    if (loginIdType === "phone") {
      if (!user_data.phone || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
    else {
      if (!user_data.email || !user_data.token) {
        setRedirect("/password/recover/");
      }
    }
  }, [user_data, setTitle, getTag, lang]);

  useEffect(() => {
    if (redirect) {
      setPageStatus(redirect);
    }
  }, [redirect, setPageStatus]);


  return (
    <div id="PasswordReset" className="form">
      <h1 className="title">{getTag("passwordreset.title")}</h1>
      <div className="form">
        <p className="description">
          {getTag("passwordreset.successful")}<br />
          {getTag("passwordreset.successful2")}
        </p>
        <div className="spacer"> </div>
        <div className="action_area">
          <p className="action_btn"><button className="btn_l acceptbtn" onClick={() => setRedirect("/login")}>{getTag("common.return")}</button></p>
        </div>
      </div>
    </div>
  );
}
